import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { RankMedal } from '../../../components/RankMedal'
import { Avatar } from '../../../components/Avatar'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { COLORS_BY_ID, type ColorId } from '../data/colors'
import { ColorCardImage } from '../components/ColorCardImage'
import {
  fetchColorfulWeeklyEntries, weekLabel,
  type ColorfulWeeklyEntryRow, type ColorfulWeeklyEventRow,
} from '../api/weekly'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import { useT } from '../../../i18n'

interface Props {
  eventId: string
  onBack: () => void
  /** Open the author's public profile when their name is tapped. */
  onOpenProfile?: (profileId: string) => void
}

type EnrichedEntry = ColorfulWeeklyEntryRow & {
  author_name: string | null
  author_avatar: string | null
}

/**
 * Leaderboard + majority-colors display for a finalized event.
 */
export function ColorfulWeeklyWinnersScreen({ eventId, onBack, onOpenProfile }: Props) {
  const t = useT()
  const [event, setEvent] = useState<ColorfulWeeklyEventRow | null>(null)
  const [entries, setEntries] = useState<EnrichedEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const { data: ev, error: e1 } = await supabase
          .from('colorful_weekly_events').select('*').eq('id', eventId).single()
        if (e1) throw e1
        if (cancelled) return
        setEvent(ev as ColorfulWeeklyEventRow)

        const rows = await fetchColorfulWeeklyEntries(eventId)
        if (cancelled) return
        setEntries(rows)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId])

  // For each subject, tally every colour that received votes and rank
  // them by count (desc) then color id (asc, deterministic on ties).
  // Shape: per-subject array of { color, count } entries, ready to
  // render as a row of mini tiles. This replaces the old "single
  // majority pick" display because seeing the full distribution is
  // more interesting — you learn that "coffee" got 6 BROWN, 2 BLACK,
  // 1 GREY rather than just "BROWN won."
  type Tally = { color: ColorId; count: number }
  const talliesPerRound: Tally[][] = (() => {
    if (!event) return []
    return event.subjects.map((_, roundIdx) => {
      const counts = new Map<string, number>()
      for (const e of entries) {
        const pick = e.picks[roundIdx]
        if (!pick) continue
        counts.set(pick, (counts.get(pick) ?? 0) + 1)
      }
      return [...counts.entries()]
        .map(([color, count]) => ({ color: color as ColorId, count }))
        .sort((a, b) => b.count - a.count || a.color.localeCompare(b.color))
    })
  })()

  // "% with the crowd" — how often this player landed on the round's
  // most-picked colour. Mirrors scoring exactly: a round with no
  // single top colour has no majority, so it counts for nobody. That
  // has to match `finalize_colorful_weekly_event` or the row reads
  // "100% with the crowd" next to a score of 3, which is how the
  // alphabetical-tiebreak bug stayed invisible for so long.
  //
  // Derived from the tallies rather than from total_score so the
  // number always means what the label says. The denominator is the
  // full subject list, not the picks they made, so a half-finished
  // entry can't post a flattering 100% off two lucky guesses.
  function crowdMatchPct(entry: EnrichedEntry): number {
    const total = event?.subjects.length ?? 0
    if (total === 0) return 0
    let hits = 0
    for (let i = 0; i < total; i++) {
      const pick = entry.picks[i]
      if (!pick) continue
      const tally = talliesPerRound[i] ?? []
      const top = tally[0]?.count ?? 0
      if (top === 0) continue
      const tiedAtTop = tally.filter(t => t.count === top).length
      if (tiedAtTop === 1 && tally[0].color === pick) hits++
    }
    return Math.round((hits / total) * 100)
  }

  if (loading || !event) {
    return (
      <div className="screen">
        <PageHeader title={t('Weekly results')} onBack={onBack} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  // Hide entries that didn't actually engage (all-null picks). These
  // were typically auto-created when a guest opened the screen but
  // never saved a colour, and rendering them as "0 of 6 picks" makes
  // it look like phantom contestants are on the leaderboard.
  const playedEntries = entries.filter(e => e.picks.some(Boolean))

  // Standard competition ranking (1, 1, 3) off the score, not the row
  // index. Ties are the normal case now that a tied round scores
  // nothing, and ranking by position handed gold and silver to two
  // players who both finished on 3.
  const ranks: number[] = []
  playedEntries.forEach((e, i) => {
    ranks.push(
      i > 0 && e.total_score === playedEntries[i - 1].total_score
        ? ranks[i - 1]
        : i + 1,
    )
  })

  return (
    <div className="screen">
      <BackgroundTitle text={t('Weekly Results')} color="#74705F" />
      <PageHeader
        title={t('Weekly results')}
        subtitle={weekLabel(event.week_start)}
        onBack={onBack}
      />

      <div className="flex-1 px-5 py-5 space-y-5 overflow-y-auto pb-6 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {!event.is_final && (
          <div className="card p-4 bg-brand-gold/10 border border-brand-gold/30">
            <p className="text-sm text-brand-text">{t('Not finalized yet. These are live numbers. Final results reveal Monday.')}</p>
          </div>
        )}

        {/* Vote distribution per subject — each colour that anyone
            picked, sized as a tiny card with a x{count} badge below. */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Crowd picks')}
          </p>
          <ul className="space-y-2">
            {event.subjects.map((s, i) => {
              const tally = talliesPerRound[i] ?? []
              return (
                <li
                  key={i}
                  className="bg-game-colorful-light border-2 border-game-colorful-dark
                             rounded-2xl shadow-card p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold w-6 text-center text-brand-muted">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-brand-text">{s}</span>
                  </div>
                  {tally.length === 0 ? (
                    <p className="mt-2 ml-9 text-xs text-brand-muted">{t('no picks')}</p>
                  ) : (() => {
                    // Share of everyone who picked THIS subject, so a
                    // subject nobody skipped and one half the room
                    // skipped both read honestly.
                    const votesThisRound = tally.reduce((n, t) => n + t.count, 0)
                    const top = tally[0].count
                    const tiedAtTop = tally.filter(t => t.count === top).length
                    return (
                      <>
                        <div className="mt-2 ml-9 flex flex-wrap gap-3">
                          {tally.map(({ color, count }) => {
                            const pct = votesThisRound > 0
                              ? Math.round((count / votesThisRound) * 100)
                              : 0
                            const colorName = COLORS_BY_ID[color]?.name ?? color
                            return (
                              <div
                                key={color}
                                className="flex flex-col items-center gap-1"
                                title={count === 1
                                  ? t('{color} · 1 vote · {pct}% of picks', { color: colorName, pct })
                                  : t('{color} · {n} votes · {pct}% of picks', { color: colorName, n: count, pct })}
                              >
                                <ColorCardImage color={color} size="sm" />
                                <span className="text-xs font-semibold text-brand-text tabular-nums">
                                  ×{count}
                                  <span className="ml-1 font-normal text-[10px] text-brand-muted">
                                    {pct}%
                                  </span>
                                </span>
                              </div>
                            )
                          })}
                        </div>
                        {/* Say so out loud. A dead-even round looks
                            identical to a scored one otherwise, which
                            is what made the old scoring so baffling. */}
                        {tiedAtTop > 1 && (
                          <p className="mt-1.5 ml-9 text-[10px] text-brand-muted">
                            {t('Tied, so no majority and no points this round')}
                          </p>
                        )}
                      </>
                    )
                  })()}
                </li>
              )
            })}
          </ul>
        </div>

        {/* Leaderboard */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Leaderboard')}
          </p>
          {playedEntries.length === 0 ? (
            <div className="card p-4 text-center text-sm text-brand-muted">
              {t('No entries this week.')}
            </div>
          ) : (
            <ul className="space-y-2">
              {playedEntries.map((e, i) => (
                <li key={e.id} className="bg-game-colorful-light border-2 border-game-colorful-dark rounded-2xl shadow-card p-3 flex items-center gap-3">
                  <RankMedal rank={ranks[i]} size="md" className="shrink-0" />
                  <Avatar name={e.author_name ?? '?'} avatarUrl={e.author_avatar} size="md" />
                  <div className="flex-1 min-w-0">
                    {onOpenProfile && e.profile_id ? (
                      <button
                        type="button"
                        onClick={() => onOpenProfile(e.profile_id)}
                        className="font-semibold text-brand-text truncate
                                   underline underline-offset-2 hover:text-game-colorful-dark
                                   text-left"
                      >
                        {e.author_name ?? t('Anonymous')}
                      </button>
                    ) : (
                      <p className="font-semibold text-brand-text truncate">
                        {e.author_name ?? t('Anonymous')}
                      </p>
                    )}
                    <p className="text-xs text-brand-muted">
                      {t('{pct}% with the crowd', { pct: crowdMatchPct(e) })}
                    </p>
                  </div>
                  <span className="font-display font-black text-brand-text">
                    {e.total_score}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
